import {io} from 'socket.io-client';
import base_url from '../baseUrls';
import ApiUtils from '../utils/apiUtils';

/** Event name for server push when data is ready (adjust to match your backend). */
export const SOCKET_EVENTS = {
  DATA_READY: 'data-ready',
};

/** sessionStorage — unique per browser tab so the server can keep multiple connections per user. */
export const BROWSER_SESSION_ID_KEY = 'ipermitBrowserSessionId';

/** @type {Map<string, { socket: import('socket.io-client').Socket, refCount: number }>} */
const socketRegistry = new Map();

function stripBearer(header) {
  if (!header || typeof header !== 'string') {
    return '';
  }
  return header.replace(/^Bearer\s+/i, '').trim();
}

function getDefaultSocketUrl() {
  return process.env.REACT_APP_SOCKET_URL || base_url.hostname || '';
}

function createBrowserSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Stable id for this browser tab/window. Sent in socket auth so the server can subscribe
 * multiple sessions for the same user (one socket per tab, not one per user globally).
 */
export function getBrowserSessionId() {
  try {
    let id = sessionStorage.getItem(BROWSER_SESSION_ID_KEY);
    if (!id) {
      id = createBrowserSessionId();
      sessionStorage.setItem(BROWSER_SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return createBrowserSessionId();
  }
}

function resolveBrowserSessionId(options = {}) {
  return options.browserSessionId || getBrowserSessionId();
}

/**
 * Socket.IO client scoped to the signed-in user and this browser tab.
 * The server should verify `auth.token`, read `auth.browserSessionId`, and join e.g.
 * `user:${userId}` (broadcast to all tabs) or track one socket per browserSessionId.
 *
 * @param {object} [options]
 * @param {string} [options.browserSessionId] — defaults to per-tab id from sessionStorage
 * @param {string} [options.url]
 * @param {string} [options.path]
 * @param {object} [options.extraAuth]
 * @param {string[]} [options.transports]
 * @returns {Promise<import('socket.io-client').Socket | null>}
 */
export async function connectUserSocket(options = {}) {
  const bearer = await ApiUtils.getAccessToken();
  const token = stripBearer(bearer);
  if (!token) {
    return null;
  }

  const url = options.url || getDefaultSocketUrl();
  if (!url) {
    console.warn('userSocket: set REACT_APP_SOCKET_URL or REACT_APP_API_BASE_URL');
    return null;
  }

  const browserSessionId = resolveBrowserSessionId(options);
  const existing = socketRegistry.get(browserSessionId);
  if (existing?.socket) {
    existing.refCount += 1;
    return existing.socket;
  }

  const path = options.path || process.env.REACT_APP_SOCKET_PATH || '/socket.io';

  const socket = io(url, {
    path,
    /**
     * A function, not an object: socket.io calls it before every connection attempt, so each
     * reconnect carries a token that is current.
     *
     * The access token is rotated on refresh and the server deletes the pair the old one belongs
     * to, so a token captured once here stops authenticating the moment the portal refreshes it.
     * The handshake only runs on connect, so the live socket kept working and nothing looked
     * wrong - but the next reconnect (a backend restart, a sleep/wake, any blip) failed
     * `Unauthorized`, and with `reconnectionAttempts: Infinity` it retried that dead token
     * forever. The tab stayed signed in, the console logged `Unauthorized`, and the agent
     * silently stopped receiving incoming-call prompts until they reloaded the page.
     */
    auth: (cb) => {
      ApiUtils.getAccessToken()
        .then((bearer) =>
          cb({
            token: stripBearer(bearer),
            browserSessionId,
            ...options.extraAuth,
          })
        )
        .catch(() =>
          // Signed out, or the refresh failed: hand over the token we came in with and let the
          // server reject it, rather than hanging the attempt on a callback that never fires.
          cb({token, browserSessionId, ...options.extraAuth})
        );
    },
    transports: options.transports || ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socketRegistry.set(browserSessionId, {socket, refCount: 1});
  return socket;
}

/** @param {string} [browserSessionId] */
export function getUserSocket(browserSessionId) {
  const id = browserSessionId || getBrowserSessionId();
  return socketRegistry.get(id)?.socket ?? null;
}

/**
 * Decrements ref count and disconnects only when no subscribers remain for this tab.
 * @param {string} [browserSessionId]
 */
export function disconnectUserSocket(browserSessionId) {
  const id = browserSessionId || getBrowserSessionId();
  const entry = socketRegistry.get(id);
  if (!entry) {
    return;
  }

  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    entry.socket.removeAllListeners();
    entry.socket.disconnect();
    socketRegistry.delete(id);
  }
}

/** @param {string} userId */
export function userRoomId(userId) {
  return userId != null && userId !== '' ? `user:${userId}` : '';
}

/** Per-tab room suffix when the server routes by browser session. */
export function userBrowserSessionRoomId(userId, browserSessionId) {
  const base = userRoomId(userId);
  const sessionId = browserSessionId || getBrowserSessionId();
  return base && sessionId ? `${base}:session:${sessionId}` : base;
}

